<script lang="ts">
export type UserVariableDefinitionOptionType = {
  dataType:
    | typeof UserVariableDefinitionDataType.MultipleSelect
    | typeof UserVariableDefinitionDataType.Select
  text: string
  value: string
  options: { text: string; value: string }[]
}
export default {}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import SegmentFormItem, {
  type SegmentItemForm,
  type SegmentItemType,
} from './SegmentFormItem.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import type { Segment } from '@/features/segment/segment'
import { nanoid } from 'nanoid'
import type {
  ChatRoomStatus,
  UserVariableDefinitionDataType,
} from '@/types/schema.generated'

const model = defineModel<{ type: 'segment'; segment: Segment }>({
  required: true,
})

const props = defineProps<{
  segmentTypeOptions: { text: string; value: SegmentItemType }[]
  inflowSourceOptions: { text: string; value: string }[]
  clickUrlOptions: { text: string; value: string }[]
  plannerEnqueteOptions: { text: string; value: string }[]
  roomStatusOptions: { text: string; value: ChatRoomStatus }[]
  userVariableDefinitionOptions: UserVariableDefinitionOptionType[]
}>()

const segments = computed({
  get: (): SegmentItemForm[] => {
    const conditions = model.value.segment.segmentConditions
    return conditions.map(condition => {
      switch (condition.type) {
        case 'line-activation-login-link':
          return {
            id: condition.id,
            itemType: 'line-activation-login-link',
            itemValue: '',
            itemNot: condition.not,
            itemOption: '',
          }
        case 'inflow-source':
          return {
            id: condition.id,
            itemType: 'inflow-source',
            itemValue: condition.inflowSourceDefinitionGUID,
            itemNot: condition.not,
            itemOption: '',
          }
        case 'click-url':
          return {
            id: condition.id,
            itemType: 'click-url',
            itemValue: condition.url,
            itemNot: condition.not,
            itemOption: '',
          }
        case 'planner-enquete-answered':
          return {
            id: condition.id,
            itemType: 'planner-enquete-answered',
            itemValue: condition.plannerEnqueteGUID,
            itemNot: condition.not,
            itemOption: '',
          }
        case 'room-status':
          return {
            id: condition.id,
            itemType: 'room-status',
            itemValue: condition.status as ChatRoomStatus,
            itemNot: condition.not,
            itemOption: '',
          }
        case 'user-variable-definition':
          return {
            id: condition.id,
            itemType: 'user-variable-definition',
            itemValue: condition.definitionGUID,
            itemNot: condition.not,
            itemOption: condition.value,
          }
      }
    })
  },
  set: (value: SegmentItemForm[]) => {
    model.value = {
      type: 'segment',
      segment: {
        segmentConditions: value.map(item => {
          switch (item.itemType) {
            case 'line-activation-login-link':
              return {
                id: item.id,
                type: 'line-activation-login-link',
                not: item.itemNot,
              }
            case 'inflow-source':
              return {
                id: item.id,
                type: 'inflow-source',
                inflowSourceDefinitionGUID: item.itemValue,
                not: item.itemNot,
              }
            case 'click-url':
              return {
                id: item.id,
                type: 'click-url',
                url: item.itemValue,
                not: item.itemNot,
              }
            case 'planner-enquete-answered':
              return {
                id: item.id,
                type: 'planner-enquete-answered',
                plannerEnqueteGUID: item.itemValue,
                not: item.itemNot,
              }
            case 'room-status':
              return {
                id: item.id,
                type: 'room-status',
                status: item.itemValue as ChatRoomStatus,
                not: item.itemNot,
              }
            case 'user-variable-definition':
              return {
                id: item.id,
                type: 'user-variable-definition',
                definitionGUID: item.itemValue,
                not: item.itemNot,
                value: item.itemOption,
              }
          }
        }),
      },
    }
  },
})

const add = () => {
  if (segments.value) {
    segments.value = [
      ...segments.value,
      {
        id: nanoid(),
        itemType: 'line-activation-login-link',
        itemValue: '',
        itemNot: false,
        itemOption: '',
      },
    ]
  }
}

const update = (i: number, value: SegmentItemForm) => {
  segments.value = segments.value.map((segment, index) =>
    index === i ? value : segment
  )
}

const remove = (i: number) => {
  segments.value = segments.value.filter((_, index) => index !== i)
}

const userVariableDefinitionOptions = computed(() =>
  props.userVariableDefinitionOptions.map(definition => {
    if (definition.options.length === 0) {
      throw new Error('選択肢がありません')
    }
    return definition
  })
)
</script>

<template>
  <section :class="$style.form">
    <div
      v-for="(segment, index) in segments"
      :key="segment.id"
      :class="[$style.form__item, $style.formItem]"
    >
      <div v-if="index > 0" :class="$style.formItem__and">AND</div>
      <SegmentFormItem
        :modelValue="segment"
        :segmentTypeOptions="props.segmentTypeOptions"
        :inflowSourceOptions="props.inflowSourceOptions"
        :clickUrlOptions="props.clickUrlOptions"
        :plannerEnqueteOptions="props.plannerEnqueteOptions"
        :roomStatusOptions="props.roomStatusOptions"
        :userVariableDefinitionOptions="userVariableDefinitionOptions"
        @update:modelValue="update(index, $event)"
        @delete="remove(index)"
        :class="$style.formItem__segment"
      />
    </div>
    <FlatButton @click="add" :class="$style.form__button">
      条件追加
    </FlatButton>
  </section>
</template>

<style module lang="scss">
.form {
  padding: 16px;

  &__item:nth-child(n + 2) {
    margin-top: 8px;
  }

  &__button {
    margin-top: 16px;
  }
}

.formItem {
  &__and {
    margin-left: 8px;
  }

  &__segment:nth-child(n + 2) {
    margin-top: 8px;
  }
}
</style>
